import { doc, setDoc, getDocs, getDoc, addDoc, updateDoc, deleteDoc, collection, query, where, serverTimestamp, onSnapshot, deleteField, arrayUnion } from 'firebase/firestore';
import { db } from './config';
import { callLookupUserByEmail } from './functions';

// Called on every sign-in. isNew=true adds a createdAt timestamp (first signup only).
export const upsertUserProfile = (user, isNew = false) =>
  setDoc(
    doc(db, 'users', user.uid),
    {
      email: user.email || '',
      displayName: user.displayName || '',
      lastLogin: serverTimestamp(),
      ...(isNew && { createdAt: serverTimestamp() }),
    },
    { merge: true }
  );

// When someone uses the app: when they last opened it and which days they used
// it (no screens or actions). userActivity/{uid}: written by its owner, read only
// by the admin. At most once an hour per person per device.
let lastActiveMark = { uid: null, at: 0 };
export const markActive = (uid) => {
  if (!uid) return;
  if (lastActiveMark.uid === uid && Date.now() - lastActiveMark.at < 60 * 60 * 1000) return;
  lastActiveMark = { uid, at: Date.now() };
  const d = new Date();
  const day = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  setDoc(doc(db, 'userActivity', uid), { lastActive: serverTimestamp(), activeDays: arrayUnion(day) }, { merge: true })
    .catch(() => { lastActiveMark = { uid: null, at: 0 }; });
};

// Admin-only: everyone's activity, keyed by uid.
export const fetchAllActivity = async () => {
  const snap = await getDocs(collection(db, 'userActivity'));
  const byUid = {};
  snap.docs.forEach((d) => { byUid[d.id] = d.data(); });
  return byUid;
};

// Admin-only: list every user that has ever signed in.
export const fetchAllUsers = async () => {
  const snap = await getDocs(collection(db, 'users'));
  return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
};

// Admin-only: delete a user's Firestore profile document.
export const deleteUserProfile = (uid) => deleteDoc(doc(db, 'users', uid));

// Fetch a single user's public profile by uid (email, displayName).
export const fetchUserById = async (uid) => {
  if (!uid) return null;
  const snap = await getDoc(doc(db, 'users', uid));
  return snap.exists() ? { id: snap.id, ...snap.data() } : null;
};

// Returns true if this is the user's very first sign-in (no profile doc exists yet).
export const checkIsNewUser = async (uid) => {
  const snap = await getDoc(doc(db, 'users', uid));
  return !snap.exists();
};

// Save or update a user's Expo push token.
// The push token goes in a private doc only its owner (and the server) can
// read. A token on the public profile would let anyone who learned a uid send
// that person notifications. The old field is cleared on the way.
export const savePushToken = async (uid, token) => {
  await setDoc(doc(db, 'userPrivate', uid), { pushToken: token, updatedAt: serverTimestamp() }, { merge: true });
  try { await setDoc(doc(db, 'users', uid), { pushToken: deleteField() }, { merge: true }); } catch (e) { /* legacy field may already be gone */ }
};

// Save the user's IANA timezone (e.g. "America/Denver") so scheduled
// reminders can fire at the right local time on whatever device they use.
export const saveUserTimezone = (uid, timezone) =>
  setDoc(doc(db, 'users', uid), { timezone }, { merge: true });

// The address people can share horses with this user at. Sign-in with Apple's
// "Hide My Email" gives an account a relay address nobody knows, and plenty of
// people sign in with an address they don't hand out — so a user may set the
// one they actually give people, and the sharing lookup tries both.
export const isRelayEmail = (email) => /@privaterelay\.appleid\.com$/i.test(email || '');

export const saveShareEmail = (uid, email) =>
  setDoc(doc(db, 'users', uid), { shareEmail: (email || '').toLowerCase().trim() }, { merge: true });

// Look up a user by email address (used for sharing). Done on the server —
// the sign-in email first, then any sharing email they set — so the rules
// never have to let a client list the users collection.
export const findUserByEmail = async (email) => {
  const needle = (email || '').toLowerCase().trim();
  if (!needle) return null;
  return callLookupUserByEmail(needle);
};

// Admin-only: list every horse across all users.
export const fetchAllHorses = async () => {
  const snap = await getDocs(collection(db, 'horses'));
  return snap.docs.map((d) => ({ id: d.id, ...d.data() })).filter((h) => !h.deleted);
};

// Get a user's uid by email (returns null if not found).
export const findUidByEmail = async (email) => {
  const found = await findUserByEmail(email);
  return found ? found.uid : null;
};

// Write an alert for the admin (works from web and native — no CORS).
export const writeAdminAlert = (data) =>
  addDoc(collection(db, 'adminAlerts'), { ...data, at: serverTimestamp(), read: false });

// Subscribe to unread admin alerts (admin native app only).
export const subscribeToAdminAlerts = (callback) => {
  const q = query(collection(db, 'adminAlerts'), where('read', '==', false));
  return onSnapshot(q, (snap) => {
    callback(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
  });
};

// Mark an admin alert as read so it doesn't re-fire.
export const markAdminAlertRead = (alertId) =>
  updateDoc(doc(db, 'adminAlerts', alertId), { read: true });
